import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'sonner';
import { UploadSimple, FilePdf, FileDoc, CheckCircle, X, WarningCircle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadCV = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [duplicateModal, setDuplicateModal] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map((file) => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      status: 'pending',
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    multiple: true,
  });

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const uploadOne = (fileObj, force) => {
    const formData = new FormData();
    formData.append('file', fileObj.file);
    formData.append('force', force ? 'true' : 'false');
    return axios.post(`${API}/upload-cv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  // Shows the duplicate-warning modal and resolves true/false based on the user's choice
  const confirmDuplicateUpload = (fileObj, existingCandidate) =>
    new Promise((resolve) => {
      setDuplicateModal({ fileObj, existingCandidate, resolve });
    });

  const uploadFiles = async () => {
    if (files.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;
    let skippedCount = 0;

    for (const fileObj of files) {
      setUploadProgress((prev) => ({ ...prev, [fileObj.id]: 'uploading' }));
      try {
        try {
          await uploadOne(fileObj, false);
          setUploadProgress((prev) => ({ ...prev, [fileObj.id]: 'success' }));
          successCount++;
        } catch (error) {
          if (error.response?.status === 409 && error.response.data?.detail?.duplicate) {
            const proceed = await confirmDuplicateUpload(fileObj, error.response.data.detail.existing_candidate);
            if (proceed) {
              await uploadOne(fileObj, true);
              setUploadProgress((prev) => ({ ...prev, [fileObj.id]: 'success' }));
              successCount++;
            } else {
              setUploadProgress((prev) => ({ ...prev, [fileObj.id]: 'skipped' }));
              skippedCount++;
            }
          } else {
            throw error;
          }
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        setUploadProgress((prev) => ({ ...prev, [fileObj.id]: 'error' }));
        errorCount++;
      }
    }

    setUploading(false);

    if (successCount > 0) {
      toast.success(`${successCount} CV(s) uploaded and parsed successfully!`);
    }
    if (skippedCount > 0) {
      toast.info(`${skippedCount} CV(s) skipped as duplicates`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} CV(s) failed to upload`);
    }

    if (successCount > 0) {
      setTimeout(() => navigate('/candidates'), 1000);
    }
  };

  return (
    <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="upload-cv-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Upload CVs
          </h1>
          <p className="text-base font-normal text-slate-600 leading-relaxed mt-2">
            Upload PDF or DOCX files to automatically extract candidate information
          </p>
        </div>

        <div
          {...getRootProps()}
          className={`upload-zone rounded-lg p-12 text-center cursor-pointer ${
            isDragActive ? 'drag-active' : ''
          }`}
          data-testid="upload-dropzone"
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center">
            <UploadSimple size={64} weight="duotone" className="text-slate-400 mb-4" />
            <p className="text-lg font-medium text-slate-800 mb-2">
              {isDragActive ? 'Drop files here' : 'Drag & drop CV files here'}
            </p>
            <p className="text-sm text-slate-600 mb-4">or click to browse files</p>
            <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">
              Supports PDF and DOCX
            </p>
          </div>
        </div>

        {files.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-medium text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Selected Files ({files.length})
              </h2>
              <button
                onClick={uploadFiles}
                disabled={uploading}
                className="btn-primary px-6 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="upload-button"
              >
                {uploading ? 'Uploading...' : 'Upload All'}
              </button>
            </div>

            <div className="space-y-3">
              {files.map((fileObj) => {
                const isPdf = fileObj.file.name.endsWith('.pdf');
                const status = uploadProgress[fileObj.id];

                return (
                  <div
                    key={fileObj.id}
                    className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg"
                    data-testid={`file-item-${fileObj.id}`}
                  >
                    <div className="flex items-center space-x-3">
                      {isPdf ? (
                        <FilePdf size={32} weight="duotone" className="text-red-500" />
                      ) : (
                        <FileDoc size={32} weight="duotone" className="text-blue-500" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-slate-900">{fileObj.file.name}</p>
                        <p className="text-xs text-slate-500">
                          {(fileObj.file.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {status === 'uploading' && (
                        <span className="text-sm text-slate-600">Uploading...</span>
                      )}
                      {status === 'success' && (
                        <CheckCircle size={24} weight="fill" className="text-green-500" />
                      )}
                      {status === 'error' && (
                        <span className="text-sm text-red-500">Failed</span>
                      )}
                      {status === 'skipped' && (
                        <span className="text-sm text-amber-600">Skipped (duplicate)</span>
                      )}
                      {!status && (
                        <button
                          onClick={() => removeFile(fileObj.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`remove-file-${fileObj.id}`}
                        >
                          <X size={20} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {duplicateModal && (
          <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="duplicate-cv-modal">
            <div className="bg-white rounded-lg max-w-lg w-full p-6">
              <div className="flex items-start space-x-3 mb-4">
                <div className="bg-amber-50 p-2.5 rounded-md">
                  <WarningCircle size={24} weight="duotone" className="text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Possible duplicate candidate
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    {`"${duplicateModal.fileObj.file.name}" matches a candidate already in the system:`}
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-5 space-y-1">
                <p className="text-sm font-medium text-slate-900">{duplicateModal.existingCandidate.name || 'Unnamed candidate'}</p>
                <p className="text-xs text-slate-600">{duplicateModal.existingCandidate.email}</p>
                {duplicateModal.existingCandidate.role && (
                  <p className="text-xs text-slate-600">Role: {duplicateModal.existingCandidate.role}</p>
                )}
                {duplicateModal.existingCandidate.uploaded_by_name && (
                  <p className="text-xs text-slate-600">
                    Uploaded by: {duplicateModal.existingCandidate.uploaded_by_name}
                  </p>
                )}
              </div>

              <p className="text-sm text-slate-700 mb-5">Do you still want to upload this CV as a new entry?</p>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => { duplicateModal.resolve(false); setDuplicateModal(null); }}
                  className="px-5 py-2 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100"
                  data-testid="duplicate-cancel-button"
                >
                  Skip this file
                </button>
                <button
                  onClick={() => { duplicateModal.resolve(true); setDuplicateModal(null); }}
                  className="btn-primary px-5 py-2 rounded-md text-sm font-medium"
                  data-testid="duplicate-confirm-button"
                >
                  Upload anyway
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadCV;